/**
 * Permission Utilities
 *
 * Role-based permission checks for project access control, composed from two
 * INDEPENDENT axes:
 *   - `ProjectRole` ("owner" | "member") — a user's role within one project,
 *     from `project_members.role`.
 *   - `TenantRole` ("admin" | "member") — a user's org-level role, from
 *     `user_tenants.role` (FLOW-632).
 *
 * This file is the single place that encodes which project actions require
 * tenant admin. The admin/member boundary may move, so no other file should
 * hard-code that decision — call these predicates instead.
 *
 * Backend counterpart: `require_project_owner_admin` in
 * `backend/src/handlers/mod.rs` is the single gate expressing the same
 * owner-AND-admin conjunction (FLOW-660 R1–R5). Change both or neither.
 */

import type {
  ProjectRole,
  TenantRole,
  TenantMembership,
  TenantId,
  ProjectMember,
  UserId,
} from "../api/types";

/** Get the current user's role in a project from the members list */
export function getUserRole(
  userId: UserId | undefined,
  members: ProjectMember[],
): ProjectRole | null {
  if (!userId) return null;
  return members.find((m) => m.user_id === userId)?.role ?? null;
}

/**
 * The caller's org role in a SPECIFIC tenant.
 *
 * Project-scoped screens must use this rather than `AuthMeResponse.tenant_role`,
 * which reports only the primary (oldest) tenant. `require_project_tenant_admin`
 * resolves the tenant from the project being acted on, so for a user in more
 * than one tenant the primary-tenant role is simply the wrong question — it
 * both hides controls the backend would allow and shows ones it would refuse.
 *
 * Fails closed: no membership row for that tenant means no role.
 */
export function tenantRoleFor(
  tenants: TenantMembership[] | undefined,
  tenantId: TenantId | undefined,
): TenantRole | null {
  if (!tenantId) return null;
  return tenants?.find((t) => t.tenant_id === tenantId)?.role ?? null;
}

/** Whether the user is an org-level admin (`user_tenants.role`). Fails closed. */
export function isTenantAdmin(
  tenantRole: TenantRole | null | undefined,
): boolean {
  return tenantRole === "admin";
}

/** Whether the user can create a project — tenant admin only, no project role involved. */
export function canCreateProject(
  tenantRole: TenantRole | null | undefined,
): boolean {
  return isTenantAdmin(tenantRole);
}

/**
 * Whether the user can change another member's org role (promote/demote
 * between `admin` and `member`). The ORG axis **alone** — no project role
 * involved — because it mirrors the backend's `require_tenant_admin`, not
 * `require_project_owner_admin`: `PATCH /api/tenants/{tenant_id}/members/{user_id}`
 * (FLOW-662) is org-scoped and takes no project membership into account.
 */
export function canManageOrgRoles(
  tenantRole: TenantRole | null | undefined,
): boolean {
  return isTenantAdmin(tenantRole);
}

/**
 * Whether the user can manage project settings (name, description, prefix).
 * Requires project ownership AND tenant admin — either alone is a 403.
 */
export function canManageProject(
  role: ProjectRole | null,
  tenantRole: TenantRole | null | undefined,
): boolean {
  return role === "owner" && isTenantAdmin(tenantRole);
}

/** Whether the user can add/remove other project members. Same gate as `canManageProject`. */
export function canManageMembers(
  role: ProjectRole | null,
  tenantRole: TenantRole | null | undefined,
): boolean {
  return role === "owner" && isTenantAdmin(tenantRole);
}

/**
 * Whether the user can invite someone by email. Same gate as
 * `canManageProject` (FLOW-659 S2) — accepting an invite grants
 * `user_tenants` membership, so sending one is org membership management,
 * not just project membership, and mirrors `require_project_owner_admin`.
 * Change both or neither.
 */
export function canInviteMembers(
  role: ProjectRole | null,
  tenantRole: TenantRole | null | undefined,
): boolean {
  return role === "owner" && isTenantAdmin(tenantRole);
}

/** Whether the user can delete the project. Same gate as `canManageProject`. */
export function canDeleteProject(
  role: ProjectRole | null,
  tenantRole: TenantRole | null | undefined,
): boolean {
  return role === "owner" && isTenantAdmin(tenantRole);
}

/**
 * Whether the user can confirm a decision — recording that the project holds
 * it true — or move an already-confirmed one back out. Same gate as
 * `canManageProject` (FLOW-659 S5, added by FLOW-672), mirroring
 * `require_project_owner_admin` on `POST /projects/{id}/decisions/{id}/confirm`.
 * Change both or neither.
 *
 * Only confirmation is gated. Writing, editing, dismissing and linking
 * decisions stay open to any project member, so this predicate must not be
 * reused to hide those.
 */
export function canConfirmDecisions(
  role: ProjectRole | null,
  tenantRole: TenantRole | null | undefined,
): boolean {
  return role === "owner" && isTenantAdmin(tenantRole);
}

/** Whether the user can leave the project (non-owner members only). Not privileged — project axis only. */
export function canLeaveProject(role: ProjectRole | null): boolean {
  return role === "member";
}
